import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { resolveAssetUrl } from '../lib/assets';
import { Gift, Printer, Loader2, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GiftCardPage() {
  const { code } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/business-page/gift-card/view/${code}`)
      .then(({ data }) => setCard(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) {
    return <div className="min-h-screen bg-[#0d0d0f] flex items-center justify-center" data-testid="giftcard-loading"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;
  }
  if (error || !card) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] flex flex-col items-center justify-center text-center px-6" data-testid="giftcard-notfound">
        <Gift className="w-14 h-14 text-white/15 mb-4" />
        <p className="text-xl font-semibold text-white/70">Dāvanu karte nav atrasta</p>
        <p className="text-white/35 mt-1">Pārbaudi, vai kods ir pareizs.</p>
      </div>
    );
  }

  const amount = Number(card.amount || 0);
  const remaining = Number(card.remaining != null ? card.remaining : amount);
  const showRemaining = card.partial_enabled && remaining < amount;
  const redeemed = card.status === 'redeemed';

  return (
    <div className="min-h-screen bg-[#0d0d0f] flex flex-col items-center justify-center px-5 py-12" data-testid="giftcard-page">
      <style>{`@media print {
        body { background: #fff !important; }
        .no-print { display: none !important; }
        .gift-print { box-shadow: none !important; margin: 0 auto !important; }
      }`}</style>

      <div className="gift-print relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl" data-testid="giftcard-card"
        style={{ background: 'linear-gradient(145deg, #1b1407 0%, #2a1d08 45%, #120d04 100%)', border: '1px solid rgba(245,197,66,0.35)' }}>
        {/* decorative glow */}
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full" style={{ background: 'radial-gradient(circle, rgba(245,197,66,0.22), transparent 70%)' }} />

        <div className="relative p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              {card.logo_url
                ? <img src={resolveAssetUrl(card.logo_url)} alt="" className="w-9 h-9 rounded-full object-cover" />
                : <Gift className="w-7 h-7 text-amber-400" />}
              <span className="text-white font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{card.business_name}</span>
            </div>
            <span className="text-amber-300/70 text-[11px] font-bold uppercase tracking-[0.2em]">Dāvanu karte</span>
          </div>

          <p className="text-amber-200/60 text-xs uppercase tracking-widest mb-1">{showRemaining ? 'Atlikums' : 'Vērtība'}</p>
          <p className="text-6xl font-light text-white leading-none mb-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="giftcard-amount">
            €{(showRemaining ? remaining : amount).toFixed(2)}
          </p>
          {showRemaining && <p className="text-white/40 text-sm mb-6">no €{amount.toFixed(2)}</p>}
          {!showRemaining && <div className="mb-6" />}

          {card.recipient_name && <p className="text-white/70 text-sm">Saņēmējs: <span className="text-white font-semibold">{card.recipient_name}</span></p>}
          {card.message && <p className="text-amber-100/80 italic text-sm mt-2">„{card.message}”</p>}

          <div className="mt-7 pt-5 border-t border-white/10">
            <p className="text-amber-200/60 text-xs uppercase tracking-widest mb-1">Kods</p>
            <p className="text-3xl font-mono font-bold tracking-[0.25em] text-white" data-testid="giftcard-code">{card.code}</p>
          </div>

          {redeemed && (
            <div className="mt-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-white/60 text-xs font-bold uppercase tracking-wider" data-testid="giftcard-redeemed">
              <Check className="w-3.5 h-3.5" /> Izmantota
            </div>
          )}
        </div>
      </div>

      <div className="no-print mt-8 flex flex-col items-center gap-3">
        <button onClick={() => window.print()}
          className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-amber-400 text-black font-bold hover:bg-amber-300 transition-colors"
          data-testid="giftcard-print-btn">
          <Printer className="w-5 h-5" /> Drukāt / Saglabāt PDF
        </button>
        <p className="text-white/35 text-xs text-center max-w-xs">Uzrādi šo kodu uzņēmumā, lai izmantotu dāvanu karti. Vari to saglabāt kā PDF vai izdrukāt.</p>
      </div>
    </div>
  );
}
